import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { categoriesAPI } from 'api/categories'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

interface Category {
 id: number
 name: string
 description?: string
 image?: string
 active: boolean
 products_count?: number
 created_at?: string
 updated_at?: string
}

interface CategoriesState {
 data: Category[]
 loading: boolean
 error: string | null
}

const initialState: CategoriesState = {
 data: [],
 loading: false,
 error: null,
}

export const fetchCategories = createAsyncThunk(
 'categories/fetchCategories',
 async (params: { search?: string; active?: string | boolean } | undefined, { rejectWithValue }) => {
 try {
 const response = await categoriesAPI.getAll(params)
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.categories.fetchFailed'))
 }
 }
)

export const createCategory = createAsyncThunk(
 'categories/createCategory',
 async (categoryData: any, { rejectWithValue }) => {
 try {
 // لازم multipart لرفع الصورة
 const response = await categoriesAPI.create(categoryData)
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.categories.createFailed'))
 }
 }
)

export const updateCategory = createAsyncThunk(
 'categories/updateCategory',
 async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
 try {
 // لازم multipart + _method=PUT (Laravel)
 const response = await categoriesAPI.update(id, data)
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.categories.updateFailed'))
 }
 }
)

export const deleteCategory = createAsyncThunk(
 'categories/deleteCategory',
 async (id: number, { rejectWithValue }) => {
 try {
 await categoriesAPI.delete(id)
 return id
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.categories.deleteFailed'))
 }
 }
)

const categoriesSlice = createSlice({
 name: 'categories',
 initialState,
 reducers: {
 clearCategories: (state) => {
 state.data = []
 state.error = null
 },
 },
 extraReducers: (builder) => {
 builder
 // Fetch categories
 .addCase(fetchCategories.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchCategories.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const data = payload?.data || payload?.result || payload
 state.data = Array.isArray(data) ? data.filter((item: any) => item != null) : []
 })
 .addCase(fetchCategories.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Create category
 .addCase(createCategory.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(createCategory.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const created = action.payload as Category & { _offlineOptimistic?: boolean }
 if (created?._offlineOptimistic || created?.id == null || !created?.name) {
 return
 }
 state.data.push(created)
 })
 .addCase(createCategory.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Update category
 .addCase(updateCategory.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(updateCategory.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const index = state.data.findIndex((cat) => cat.id === (action.payload as any).id)
 if (index !== -1) {
 state.data[index] = action.payload as any
 }
 })
 .addCase(updateCategory.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Delete category
 .addCase(deleteCategory.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(deleteCategory.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 state.data = state.data.filter((cat) => cat.id !== action.payload)
 })
 .addCase(deleteCategory.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearCategories } = categoriesSlice.actions
export default categoriesSlice.reducer
